import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import Button from '../components/common/Button'

const STATUS_COLORS = {
    'InProgress': { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
    'Awarded': { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
    'Pending': { bg: 'bg-green-50', text: 'text-green-700', label: 'In Progress' },
    'Closed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Closed' },
    'None': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Quoted' },
    'NotAwarded': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Awarded' },
}

function getStatusStyle(status) {
    return STATUS_COLORS[status] || { bg: 'bg-white', text: 'text-gray-800', label: status || '-' }
}

function getRowClass(status, isActive) {
    if (!isActive) {
        return 'bg-yellow-50 hover:bg-yellow-100'
    }
    switch (status) {
        case 'InProgress':
        case 'Awarded':
        case 'Pending':
            return 'bg-green-50 hover:bg-green-100'
        case 'Closed':
            return 'bg-red-50 hover:bg-red-100'
        case 'None':
            return 'bg-blue-50 hover:bg-blue-100'
        case 'NotAwarded':
            return 'bg-gray-50 hover:bg-gray-100'
        default:
            return 'hover:bg-gray-50'
    }
}

export default function JobList() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [jobs, setJobs] = useState([])
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [page, setPage] = useState(1)
    const perPage = 50

    useEffect(() => {
        loadJobs()
    }, [])

    async function loadJobs() {
        setLoading(true)
        try {
            const response = await api.get('/api/jobs/list')
            if (response.success) {
                setJobs(response.jobs || [])
            }
        } catch (error) {
            console.error('Error loading jobs:', error)
        }
        setLoading(false)
    }

    // Status filter options
    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'inprogress', label: 'In Progress' },
        { value: 'Closed', label: 'Closed' },
        { value: 'None', label: 'Quoted' },
        { value: 'NotAwarded', label: 'Not Awarded' },
        { value: 'inactive', label: 'Inactive' },
    ]

    // Filter jobs
    const filtered = jobs.filter(job => {
        const matchesSearch = !search ||
            (job.description || '').toLowerCase().includes(search.toLowerCase()) ||
            (job.customer || '').toLowerCase().includes(search.toLowerCase()) ||
            (job.jobNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (job.contact || '').toLowerCase().includes(search.toLowerCase())

        let matchesStatus = true
        if (statusFilter === 'inprogress') {
            matchesStatus = job.isActive !== false && ['InProgress', 'Awarded', 'Pending'].includes(job.status)
        } else if (statusFilter === 'inactive') {
            matchesStatus = job.isActive === false
        } else if (statusFilter !== 'all') {
            matchesStatus = job.status === statusFilter
        }

        return matchesSearch && matchesStatus
    })

    // Paginate
    const totalPages = Math.ceil(filtered.length / perPage)
    const paginated = filtered.slice((page - 1) * perPage, page * perPage)

    // Reset page when filters change
    useEffect(() => {
        setPage(1)
    }, [search, statusFilter])

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Jobs</h1>
                    <p className="text-blue-100 mt-1">{filtered.length} jobs</p>
                </div>
                <Button variant="secondary" onClick={() => navigate('/')}>
                    ← Dashboard
                </Button>
            </div>

            {/* Legend */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-4">
                <div className="flex flex-wrap gap-4 items-center text-sm">
                    <span className="font-medium text-gray-600">Status:</span>
                    <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-green-200 inline-block"></span>
                        In Progress
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-blue-200 inline-block"></span>
                        Quoted
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-red-200 inline-block"></span>
                        Closed
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-gray-200 inline-block"></span>
                        Not Awarded
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-yellow-200 inline-block"></span>
                        Inactive
                    </span>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="sm:col-span-3">
                        <input
                            type="text"
                            placeholder="Search by description, customer, contact..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-4 py-3 text-base rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-4 py-3 text-base rounded-lg border border-gray-300"
                    >
                        {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-rdi-blue text-white">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Job Description</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Company</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold hidden md:table-cell">Contact</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="text-center py-12 text-gray-500">
                                        No jobs found
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((job, index) => (
                                    <tr
                                        key={index}
                                        className={`${getRowClass(job.status, job.isActive)} transition-colors`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className={`text-sm font-semibold ${job.isActive === false ? 'text-gray-500' : 'text-rdi-blue'}`}>
                                                {job.description || 'Untitled Job'}
                                            </div>
                                            {job.jobNumber && (
                                                <div className="text-xs text-gray-400 mt-0.5">{job.jobNumber}</div>
                                            )}
                                            {job.isActive === false && (
                                                <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded font-medium">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className={`text-sm ${job.isActive === false ? 'text-gray-500' : ''}`}>
                                                {job.customer}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm hidden md:table-cell">
                                            {job.contact || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                        <p className="text-sm text-gray-600">
                            Showing {(page - 1) * perPage + 1} - {Math.min(page * perPage, filtered.length)} of {filtered.length}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-100"
                            >
                                ← Prev
                            </button>
                            <span className="px-3 py-1 text-sm">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-100"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}